import CheckIcon from '@assets/icons/check.svg?react'
import ChevronRightIcon from '@assets/icons/chevron-right.svg?react'
import SearchIcon from '@assets/icons/search.svg?react'
import { CoreInput, CoreProfileAvatar } from '@components'
import cn from 'classnames'
import type { MerchantSwitchResponse } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import { useMemo, useState } from 'react'
import styles from './ProfileMenu.module.scss'

// Beyond this many companies the list scrolls, so a search field earns its space.
const SEARCH_THRESHOLD = 5

const normalize = (value?: string) => (value ?? '').replace(/\s/g, '').toLowerCase()

type CompanySwitcherProps = {
  companies: MerchantSwitchResponse[]
  currentMerchantId?: number
  parentMerchantId?: number
  onSelect: (merchantId: number) => void
}

export const CompanySwitcher = ({ companies, currentMerchantId, parentMerchantId, onSelect }: CompanySwitcherProps) => {
  const [terms, setTerms] = useState('')

  const visibleCompanies = useMemo(() => {
    const query = normalize(terms)
    return companies
      .filter(company => normalize(company.name).includes(query))
      .sort((a, b) => Number(b.merchantId === parentMerchantId) - Number(a.merchantId === parentMerchantId))
  }, [companies, terms, parentMerchantId])

  return (
    <div className={cn(styles['profile-menu-section'], 'profile-menu-section')} data-test='profile-menu-companies'>
      <span className={cn(styles['profile-menu-section-label'], 'profile-menu-section-label')}>
        {tr('FRONTEND.PROFILE_MENU.SWITCH_COMPANY', 'Switch company')}
      </span>

      {companies.length > SEARCH_THRESHOLD && (
        <div className={cn(styles['profile-menu-search'], 'profile-menu-search')}>
          <CoreInput
            data-test='search'
            type='text'
            label={tr('FRONTEND.HEADER.SEARCH', 'search')}
            value={terms}
            size='small'
            LeftIcon={<SearchIcon />}
            onChange={(_: string, value: string) => setTerms(value)}
          />
        </div>
      )}

      <div className={cn(styles['profile-menu-companies'], 'profile-menu-companies')}>
        {visibleCompanies.length === 0 && (
          <span className={cn(styles['profile-menu-empty'], 'profile-menu-empty')}>
            {tr('FRONTEND.PROFILE_SECTION.RESULT_NOT_FOUND', 'Result not found')}
          </span>
        )}

        {visibleCompanies.map(({ merchantId, name }) => {
          const isCurrent = merchantId === currentMerchantId
          const tag =
            merchantId === parentMerchantId
              ? tr('FRONTEND.PROFILE_MENU.PARENT_COMPANY', 'Parent company')
              : tr('FRONTEND.PROFILE_MENU.SUB_MERCHANT', 'Sub-merchant')

          const content = (
            <>
              <CoreProfileAvatar name={name} />
              <span className={cn(styles['profile-menu-company-text'], 'profile-menu-company-text')}>
                <span className={cn(styles['profile-menu-company-name'], 'profile-menu-company-name')}>{name}</span>
                <span className={cn(styles['profile-menu-company-tag'], 'profile-menu-company-tag')}>{tag}</span>
              </span>
              <span className={cn(styles['profile-menu-company-icon'], 'profile-menu-company-icon')}>
                {isCurrent ? <CheckIcon /> : <ChevronRightIcon />}
              </span>
            </>
          )

          return isCurrent ? (
            <div
              key={merchantId}
              aria-current='true'
              className={cn(styles['profile-menu-company'], 'profile-menu-company', 'profile-menu-company-current')}
              data-test={`profile-menu-company-${merchantId}`}
            >
              {content}
            </div>
          ) : (
            <button
              key={merchantId}
              type='button'
              className={cn(styles['profile-menu-company'], 'profile-menu-company', 'profile-menu-company-selectable')}
              onClick={() => merchantId !== undefined && onSelect(merchantId)}
              data-test={`profile-menu-company-${merchantId}`}
            >
              {content}
            </button>
          )
        })}
      </div>
    </div>
  )
}
